import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowRight, Calendar, Users, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

import SEO from '@/components/SEO';

const DestinationsPage = () => {
  const destinations = [
    {
      name: 'Victoria Falls',
      country: 'Zimbabwe & Zambia',
      description: 'The "Smoke that Thunders". Experience the world\'s largest waterfall alongside high-octane adrenaline sports and luxury river cruises on the Zambezi.',
      image: 'https://images.unsplash.com/photo-1627664819818-e147d6221422?auto=format&fit=crop&w=1000&q=80',
      highlights: ['Waterfall Tours', 'Helicopter Flights', 'Bungee Jumping', 'Sunset Cruises'],
      bestTime: 'Feb - July (High Water)',
      duration: '2-4 days',
      linkedCategory: 'Adrenaline Activities',
      placeFilter: 'All'
    },
    {
      name: 'South Luangwa',
      country: 'Zambia',
      description: 'The birthplace of the walking safari. Witness incredible leopard concentrations and diverse wildlife in one of Africa\'s most pristine national parks.',
      image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
      highlights: ['Walking Safaris', 'Leopard Tracking', 'Night Game Drives'],
      bestTime: 'June - October',
      duration: '4-6 days',
      linkedCategory: 'Safari Activities',
      placeFilter: 'Zambia'
    },
    {
      name: 'Chobe National Park',
      country: 'Botswana',
      description: 'Renowned for having the highest concentration of elephants in Africa. Spectacular boat-based game viewing along the Chobe River.',
      image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80',
      highlights: ['Chobe Day Trip', 'River Boat Safaris', 'Elephant Tracking'],
      bestTime: 'April - October',
      duration: '1-3 days',
      linkedCategory: 'Day Trips',
      placeFilter: 'Botswana'
    },
    {
      name: 'Mana Pools',
      country: 'Zimbabwe',
      description: 'A UNESCO World Heritage site known for its remote wilderness. Stand face-to-face with elephants on foot or paddle the Zambezi in a canoe.',
      image: 'https://images.unsplash.com/photo-1512411993213-39d67568198f?auto=format&fit=crop&w=1000&q=80',
      highlights: ['Canoeing Safaris', 'Walking Safaris', 'Wild Dog Tracking'],
      bestTime: 'May - October',
      duration: '3-5 days',
      linkedCategory: 'Safari Activities',
      placeFilter: 'Zimbabwe'
    },
    {
      name: 'Maasai Mara',
      country: 'Kenya',
      description: 'The stage for the Great Migration. Experience vast savannas, iconic big cats, and authentic Maasai cultural encounters.',
      image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1000&q=80',
      highlights: ['Great Migration', 'Hot Air Balloons', 'Maasai Village Tour'],
      bestTime: 'July - October',
      duration: '4-7 days',
      linkedCategory: 'Air Activities',
      placeFilter: 'All'
    }
  ];
  // ... (rest of the file content until return)

  return (
    <div className="min-h-screen">
      <SEO
        title="Destinations"
        description="Explore top safari destinations: Victoria Falls, Chobe, South Luangwa, Maasai Mara & more. Plan your dream trip with Peregrine Tours."
        keywords="Victoria Falls, Chobe National Park, Maasai Mara, South Luangwa, Mana Pools, Safari Destinations"
        canonicalUrl="https://www.peregrinetoursandsafaris.com/destinations"
        schema={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "African Safari Destinations",
          "description": "A list of iconic safari destinations in Southern and Eastern Africa.",
          "itemListElement": destinations.map((dest, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Place",
              "name": dest.name,
              "description": dest.description,
              "image": dest.image,
              "address": {
                "@type": "PostalAddress",
                "addressCountry": dest.country
              }
            }
          }))
        }}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
              Explore Our Destinations
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-serif leading-tight">
              Where Adventure Meets the Wild
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Explore the iconic landscapes of Southern and Eastern Africa. Each destination is paired with curated activities to ensure an unforgettable journey.
            </p>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinations.map((destination, index) => (
              <Card
                key={index}
                className="group overflow-hidden border-2 hover:border-primary/30 transition-all duration-300 flex flex-col"
              >
                {/* Image Section */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />

                  <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">{destination.country}</span>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-background font-serif">
                      {destination.name}
                    </h3>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                    {destination.description}
                  </p>

                  {/* Quick Info */}
                  <div className="grid grid-cols-2 gap-4 mb-6 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span>{destination.bestTime}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span>{destination.duration}</span>
                    </div>
                  </div>

                  {/* Highlights / Activities Link */}
                  <div className="mb-6">
                    <h4 className="text-xs font-bold text-foreground mb-3 uppercase tracking-widest flex items-center gap-2">
                      <Tag className="w-3 h-3 text-accent" />
                      Must-Do Activities
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {destination.highlights.map((highlight, idx) => (
                        <span
                          key={idx}
                          className="text-[10px] px-2 py-1 bg-muted text-muted-foreground rounded border border-border"
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="mt-auto space-y-2">
                    <Button
                      asChild
                      className="w-full bg-primary hover:bg-primary-dark text-primary-foreground"
                    >
                      <Link to={`/activities?place=${destination.placeFilter}`}>View All Activities</Link>
                    </Button>
                    <Button
                      asChild
                      variant="ghost"
                      className="w-full justify-center group/btn hover:bg-primary/5"
                    >
                      <Link to="/contact" className="flex items-center gap-2">
                        <span className="text-primary font-medium">Plan Trip</span>
                        <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <Card className="p-12 text-center border-none shadow-xl bg-background">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
              Can't Decide Where to Go?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Our travel specialists can help you combine these destinations into a seamless multi-country safari experience.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-accent hover:bg-accent-dark text-accent-foreground px-8"
            >
              <Link to="/contact">Speak to an Expert</Link>
            </Button>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DestinationsPage;